/**
 * Web向けUIプリミティブ
 * 旧React Native画面の表示契約をDOM要素へ変換し、純React上で同じ画面構造を提供します。
 */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toScaledTextLength } from "../utils/textScale";

/** 任意のWebプリミティブ用プロパティ */
type PrimitiveProps = Record<string, any> & {
  children?: React.ReactNode;
  className?: string;
  style?: any;
};

/** React Native互換のスタイル型 */
export type ViewStyle = React.CSSProperties & Record<string, any>;

/** アニメーション値または補間値の共通契約 */
type AnimatedResolvable = {
  __isWebAnimatedValue: true;
  getValue: () => number;
  subscribe: (listener: () => void) => () => void;
};

/**
 * 数値範囲内の補間値を算出する
 * @param {number} value - 入力値
 * @param {number[]} inputRange - 入力範囲
 * @param {number[]} outputRange - 出力範囲
 * @returns {number} 補間結果
 */
const interpolateNumber = (
  value: number,
  inputRange: number[],
  outputRange: number[],
) => {
  const lastIndex = Math.min(inputRange.length, outputRange.length) - 1;
  if (lastIndex <= 0) {
    return Number(outputRange[0] ?? 0);
  }

  let upperIndex = inputRange.findIndex((inputValue) => value <= inputValue);
  if (upperIndex <= 0) {
    upperIndex = 1;
  } else if (upperIndex < 0) {
    upperIndex = lastIndex;
  }

  const lowerIndex = Math.max(0, upperIndex - 1);
  const inputStart = Number(inputRange[lowerIndex] ?? 0);
  const inputEnd = Number(inputRange[upperIndex] ?? inputStart);
  const outputStart = Number(outputRange[lowerIndex] ?? 0);
  const outputEnd = Number(outputRange[upperIndex] ?? outputStart);
  const ratio =
    inputEnd === inputStart
      ? 0
      : Math.min(1, Math.max(0, (value - inputStart) / (inputEnd - inputStart)));

  return outputStart + (outputEnd - outputStart) * ratio;
};

/** Web向けアニメーション数値 */
class WebAnimatedValue implements AnimatedResolvable {
  __isWebAnimatedValue = true as const;

  /** 現在値 */
  private value: number;

  /** 値変更の購読者 */
  private listeners = new Set<() => void>();

  /**
   * アニメーション値を生成する
   * @param {number} initialValue - 初期値
   */
  constructor(initialValue: number) {
    this.value = Number(initialValue) || 0;
  }

  /**
   * 現在値を返す
   * @returns {number} 現在値
   */
  getValue = () => this.value;

  /**
   * 現在値を更新する
   * @param {number} nextValue - 更新後の値
   * @returns {void}
   */
  setValue = (nextValue: number) => {
    this.value = Number(nextValue) || 0;
    this.listeners.forEach((listener) => listener());
  };

  /**
   * 値変更を購読する
   * @param {() => void} listener - 変更時処理
   * @returns {() => void} 購読解除処理
   */
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * 入出力範囲による補間値を生成する
   * @param {{inputRange: number[], outputRange: number[]}} config - 補間設定
   * @returns {AnimatedResolvable} 補間値
   */
  interpolate = (config: {
    inputRange: number[];
    outputRange: number[];
  }): AnimatedResolvable => ({
    __isWebAnimatedValue: true,
    getValue: () =>
      interpolateNumber(this.value, config.inputRange, config.outputRange),
    subscribe: this.subscribe,
  });
}

/**
 * アニメーション可能な値か判定する
 * @param {unknown} value - 判定対象
 * @returns {value is AnimatedResolvable} アニメーション値の場合true
 */
const isAnimatedResolvable = (value: unknown): value is AnimatedResolvable =>
  Boolean(
    value &&
      typeof value === "object" &&
      (value as AnimatedResolvable).__isWebAnimatedValue,
  );

/**
 * React Native配列スタイルを単一オブジェクトへ展開する
 * @param {unknown} style - スタイル指定
 * @returns {Record<string, any>} 展開済みスタイル
 */
const flattenStyle = (style: unknown): Record<string, any> => {
  if (Array.isArray(style)) {
    return style.reduce(
      (mergedStyle, item) => ({ ...mergedStyle, ...flattenStyle(item) }),
      {},
    );
  }

  if (typeof style === "function") {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "[WebPrimitives] 関数形式のstyleはWebでは適用されません。Tailwind CSSのclassNameを使用してください。",
      );
    }
    return {};
  }

  if (!style || typeof style !== "object") {
    return {};
  }

  return { ...(style as Record<string, any>) };
};

/**
 * アニメーション値を実数へ変換する
 * @param {unknown} value - 対象値
 * @returns {unknown} 解決済み値
 */
const resolveAnimatedValue = (value: unknown) =>
  isAnimatedResolvable(value) ? value.getValue() : value;

/**
 * React Native形式のtransformをCSS文字列へ変換する
 * @param {unknown} transformValue - transform配列
 * @returns {string | undefined} CSS transform
 */
const normalizeTransform = (transformValue: unknown) => {
  if (!Array.isArray(transformValue)) {
    return undefined;
  }

  return transformValue
    .flatMap((transformItem) =>
      Object.entries(transformItem ?? {}).map(([transformName, rawValue]) => {
        const value = resolveAnimatedValue(rawValue);
        if (
          ["translateX", "translateY"].includes(transformName) &&
          typeof value === "number"
        ) {
          return `${transformName}(${value}px)`;
        }
        if (transformName === "rotate" && typeof value === "number") {
          return `rotate(${value}deg)`;
        }
        return `${transformName}(${String(value)})`;
      }),
    )
    .join(" ");
};

/**
 * React Native形式のスタイルをDOM向けCSSへ変換する
 * @param {unknown} style - React Native形式スタイル
 * @returns {React.CSSProperties} DOM向けスタイル
 */
const normalizeStyle = (style: unknown): React.CSSProperties => {
  const sourceStyle = flattenStyle(style);
  const normalizedStyle: Record<string, any> = {};

  Object.entries(sourceStyle).forEach(([key, rawValue]) => {
    const value = resolveAnimatedValue(rawValue);

    if (key === "transform") {
      normalizedStyle.transform = normalizeTransform(rawValue);
      return;
    }

    if (key === "paddingHorizontal") {
      normalizedStyle.paddingLeft = value;
      normalizedStyle.paddingRight = value;
      return;
    }
    if (key === "paddingVertical") {
      normalizedStyle.paddingTop = value;
      normalizedStyle.paddingBottom = value;
      return;
    }
    if (
      (key === "fontSize" || key === "lineHeight") &&
      (typeof value === "number" || typeof value === "string")
    ) {
      // 文字の大きさの設定に従うよう倍率を掛ける。React Native形式の数値lineHeightはpx相当だが、
      // Reactのインラインstyleは単位なし数値を「フォントサイズ比の倍率」として解釈するため、pxとして扱う
      normalizedStyle[key] = toScaledTextLength(value);
      return;
    }
    if (key === "textShadowColor" || key === "textShadowOffset" || key === "textShadowRadius") {
      return;
    }
    if (
      key === "shadowColor" ||
      key === "shadowOffset" ||
      key === "shadowOpacity" ||
      key === "shadowRadius" ||
      key === "elevation"
    ) {
      return;
    }

    normalizedStyle[key] = value;
  });

  if (sourceStyle.textShadowColor) {
    const offset = sourceStyle.textShadowOffset ?? {};
    normalizedStyle.textShadow = `${offset.width ?? 0}px ${offset.height ?? 0}px ${sourceStyle.textShadowRadius ?? 0}px ${sourceStyle.textShadowColor}`;
  }

  if (sourceStyle.shadowColor || sourceStyle.elevation) {
    const offset = sourceStyle.shadowOffset ?? {};
    const opacity = sourceStyle.shadowOpacity ?? 0.28;
    const color = sourceStyle.shadowColor ?? "#000000";
    normalizedStyle.boxShadow = `${offset.width ?? 0}px ${offset.height ?? Math.max(2, Number(sourceStyle.elevation ?? 2))}px ${sourceStyle.shadowRadius ?? Math.max(4, Number(sourceStyle.elevation ?? 2) * 2)}px color-mix(in srgb, ${color} ${Math.round(opacity * 100)}%, transparent)`;
  }

  return normalizedStyle as React.CSSProperties;
};

/**
 * スタイル内のアニメーション値を抽出する
 * @param {unknown} style - スタイル
 * @returns {AnimatedResolvable[]} アニメーション値一覧
 */
const collectAnimatedValues = (style: unknown): AnimatedResolvable[] => {
  const values: AnimatedResolvable[] = [];

  const visit = (currentValue: unknown) => {
    if (isAnimatedResolvable(currentValue)) {
      values.push(currentValue);
      return;
    }
    if (Array.isArray(currentValue)) {
      currentValue.forEach(visit);
      return;
    }
    if (currentValue && typeof currentValue === "object") {
      Object.values(currentValue as Record<string, unknown>).forEach(visit);
    }
  };

  visit(style);
  return values;
};

/**
 * 共通アクセシビリティ属性をDOM属性へ変換する
 * @param {PrimitiveProps} props - 元プロパティ
 * @returns {Record<string, any>} DOM属性
 */
const buildDomProps = (props: PrimitiveProps) => {
  const {
    accessibilityHint,
    accessibilityLabel,
    accessibilityLiveRegion,
    accessibilityRole,
    accessibilityState,
    accessible,
    onPress,
    pointerEvents,
    testID,
    hitSlop,
    ...remainingProps
  } = props;

  const domProps: Record<string, any> = {
    ...remainingProps,
    "aria-label": accessibilityLabel ?? remainingProps["aria-label"],
    "aria-description": accessibilityHint,
    "aria-live":
      accessibilityLiveRegion === "polite" ||
      accessibilityLiveRegion === "assertive"
        ? accessibilityLiveRegion
        : undefined,
    "data-testid": testID,
    role:
      remainingProps.role ??
      (accessibilityRole === "header" ? "heading" : accessibilityRole),
    style: {
      ...normalizeStyle(remainingProps.style),
      ...(pointerEvents ? { pointerEvents } : {}),
    },
  };

  if (onPress) {
    domProps.onClick = onPress;
  }
  if (accessible === false) {
    domProps["aria-hidden"] = true;
  }
  if (accessibilityState) {
    domProps["aria-disabled"] = accessibilityState.disabled;
    domProps["aria-selected"] = accessibilityState.selected;
    domProps["aria-expanded"] = accessibilityState.expanded;
    domProps["aria-checked"] = accessibilityState.checked;
    domProps["aria-busy"] = accessibilityState.busy;
  }

  Object.keys(domProps).forEach((key) => {
    if (domProps[key] === undefined) {
      delete domProps[key];
    }
  });

  return domProps;
};

/**
 * レイアウト用div
 * @returns {JSX.Element} DOMレイアウト
 */
export const View = forwardRef<HTMLDivElement, PrimitiveProps>(
  ({ children, className = "", ...props }, ref) => (
    <div
      {...buildDomProps(props)}
      className={`web-view ${className}`.trim()}
      ref={ref}
    >
      {children}
    </div>
  ),
);
View.displayName = "View";

/**
 * テキスト用span
 * @returns {JSX.Element} DOMテキスト
 */
export const Text = forwardRef<HTMLSpanElement, PrimitiveProps>(
  (
    {
      children,
      className = "",
      numberOfLines,
      selectable,
      ...props
    },
    ref,
  ) => {
    const lineStyle =
      Number(numberOfLines) > 0
        ? {
            WebkitBoxOrient: "vertical" as const,
            WebkitLineClamp: Number(numberOfLines),
            display: "-webkit-box",
            overflow: "hidden",
          }
        : {};

    return (
      <span
        {...buildDomProps({
          ...props,
          style: {
            ...normalizeStyle(props.style),
            ...lineStyle,
            ...(selectable === false ? { userSelect: "none" } : {}),
          },
        })}
        className={`web-text ${className}`.trim()}
        ref={ref}
      >
        {children}
      </span>
    );
  },
);
Text.displayName = "Text";

/**
 * 押下可能なbutton
 * @returns {JSX.Element} DOMボタン
 */
export const Pressable = forwardRef<HTMLButtonElement, PrimitiveProps>(
  (
    {
      children,
      className = "",
      disabled = false,
      onPress,
      type = "button",
      ...props
    },
    ref,
  ) => (
    <button
      {...buildDomProps({ ...props, onPress })}
      className={`web-pressable ${className}`.trim()}
      disabled={disabled}
      ref={ref}
      type={type}
    >
      {children}
    </button>
  ),
);
Pressable.displayName = "Pressable";

/**
 * スクロール領域
 * @returns {JSX.Element} DOMスクロール領域
 */
export const ScrollView = forwardRef<any, PrimitiveProps>(
  (
    {
      children,
      className = "",
      contentContainerStyle,
      contentContainerClassName = "",
      horizontal = false,
      keyboardShouldPersistTaps,
      onLayout,
      pagingEnabled,
      scrollEnabled = true,
      showsHorizontalScrollIndicator,
      showsVerticalScrollIndicator,
      ...props
    },
    forwardedRef,
  ) => {
    const elementRef = useRef<HTMLDivElement | null>(null);

    useImperativeHandle(forwardedRef, () => ({
      /**
       * 指定位置へスクロールする
       * @param {{x?: number, y?: number, animated?: boolean}} options - スクロール設定
       * @returns {void}
       */
      scrollTo: (options: { x?: number; y?: number; animated?: boolean }) => {
        elementRef.current?.scrollTo({
          left: options?.x ?? 0,
          top: options?.y ?? 0,
          behavior: options?.animated ? "smooth" : "auto",
        });
      },
    }));

    useEffect(() => {
      if (!onLayout || !elementRef.current) {
        return undefined;
      }

      const notifyLayout = () => {
        if (!elementRef.current) {
          return;
        }
        const rectangle = elementRef.current.getBoundingClientRect();
        onLayout({
          nativeEvent: {
            layout: {
              height: rectangle.height,
              width: rectangle.width,
              x: rectangle.x,
              y: rectangle.y,
            },
          },
        });
      };

      notifyLayout();
      const observer =
        typeof ResizeObserver !== "undefined"
          ? new ResizeObserver(notifyLayout)
          : null;
      observer?.observe(elementRef.current);
      return () => observer?.disconnect();
    }, [onLayout]);

    return (
      <div
        {...buildDomProps({
          ...props,
          style: {
            ...normalizeStyle(props.style),
            overflowX: horizontal ? "auto" : "hidden",
            overflowY: horizontal ? "hidden" : "auto",
            pointerEvents: scrollEnabled ? undefined : "none",
            scrollbarWidth:
              showsHorizontalScrollIndicator === false ||
              showsVerticalScrollIndicator === false
                ? "none"
                : undefined,
          },
        })}
        className={`web-scroll-view ${className}`.trim()}
        ref={elementRef}
      >
        <div
          className={
            horizontal
              ? `web-scroll-content web-scroll-content-horizontal ${contentContainerClassName}`
              : `web-scroll-content ${contentContainerClassName}`
          }
          style={normalizeStyle(contentContainerStyle)}
        >
          {children}
        </div>
      </div>
    );
  },
);
ScrollView.displayName = "ScrollView";

/**
 * 入力欄
 * @returns {JSX.Element} DOM入力欄
 */
export const TextInput = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  PrimitiveProps
>(
  (
    {
      className = "",
      editable = true,
      multiline = false,
      autoCorrect,
      onChangeText,
      onSubmitEditing,
      placeholderTextColor,
      secureTextEntry = false,
      style,
      textAlignVertical,
      value,
      ...props
    },
    ref,
  ) => {
    const commonProps: Record<string, any> = {
      ...buildDomProps({
        ...props,
        style: {
          ...normalizeStyle(style),
          "--web-placeholder-color": placeholderTextColor,
        },
      }),
      className: `web-text-input ${className}`.trim(),
      autoCorrect:
        typeof autoCorrect === "boolean"
          ? autoCorrect
            ? "on"
            : "off"
          : autoCorrect,
      disabled: !editable || props.disabled,
      onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChangeText?.(event.target.value),
      onKeyDown: (
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
      ) => {
        props.onKeyDown?.(event);
        if (event.key === "Enter" && !multiline) {
          onSubmitEditing?.({ nativeEvent: { text: event.currentTarget.value } });
        }
      },
      value: value ?? "",
    };

    if (multiline) {
      return <textarea {...commonProps} ref={ref as React.Ref<HTMLTextAreaElement>} />;
    }

    return (
      <input
        {...commonProps}
        ref={ref as React.Ref<HTMLInputElement>}
        type={secureTextEntry ? "password" : props.type ?? "text"}
      />
    );
  },
);
TextInput.displayName = "TextInput";

/**
 * 画像要素
 * @returns {JSX.Element} DOM画像
 */
export const Image = forwardRef<HTMLImageElement, PrimitiveProps>(
  (
    {
      accessibilityLabel,
      className = "",
      resizeMode = "cover",
      source,
      style,
      ...props
    },
    ref,
  ) => {
    const sourceValue =
      typeof source === "string"
        ? source
        : source?.uri ?? source?.default ?? source?.src ?? "";

    return (
      <img
        {...buildDomProps({
          ...props,
          accessibilityLabel,
          style: {
            ...normalizeStyle(style),
            objectFit: resizeMode === "contain" ? "contain" : "cover",
          },
        })}
        alt={accessibilityLabel ?? ""}
        className={`web-image ${className}`.trim()}
        ref={ref}
        src={sourceValue}
      />
    );
  },
);
Image.displayName = "Image";

/** 表示中のModalのID。後ろほど手前に重なっている */
let openModalIds: string[] = [];

/** 背景として隠している要素の、隠しているModalの数と隠す前の状態 */
type HiddenBackgroundState = {
  count: number;
  ariaHidden: string | null;
  inert: boolean;
};

/**
 * Modalが背景として隠している要素
 * 重なったModalが閉じる順（画面ごと離れたときのReactの後始末の順を含む）に関係なく、
 * 最後の1枚が閉じたときだけ元の状態へ戻すため、Modalごとではなくここで数える
 */
let hiddenBackgroundStates = new Map<HTMLElement, HiddenBackgroundState>();

/** 最初のModalを開く前のbodyのoverflow */
let bodyOverflowBeforeModals: string | null = null;

/**
 * 要素を背景として隠す。別のModalがすでに隠している場合は数だけ増やす
 * @param {HTMLElement} element - body直下の要素
 * @returns {void}
 */
const hideBackgroundElement = (element: HTMLElement) => {
  const hiddenState = hiddenBackgroundStates.get(element);
  if (hiddenState) {
    hiddenBackgroundStates = new Map(hiddenBackgroundStates).set(element, {
      ...hiddenState,
      count: hiddenState.count + 1,
    });
    return;
  }

  hiddenBackgroundStates = new Map(hiddenBackgroundStates).set(element, {
    count: 1,
    ariaHidden: element.getAttribute("aria-hidden"),
    inert: Boolean((element as HTMLElement & { inert?: boolean }).inert),
  });
  element.setAttribute("aria-hidden", "true");
  (element as HTMLElement & { inert?: boolean }).inert = true;
};

/**
 * 背景として隠していた要素を1枚分放す。隠しているModalが無くなったら元の状態へ戻す
 * @param {HTMLElement} element - body直下の要素
 * @returns {void}
 */
const releaseBackgroundElement = (element: HTMLElement) => {
  const hiddenState = hiddenBackgroundStates.get(element);
  if (!hiddenState) {
    return;
  }

  const nextHiddenBackgroundStates = new Map(hiddenBackgroundStates);
  if (hiddenState.count > 1) {
    hiddenBackgroundStates = nextHiddenBackgroundStates.set(element, {
      ...hiddenState,
      count: hiddenState.count - 1,
    });
    return;
  }

  nextHiddenBackgroundStates.delete(element);
  hiddenBackgroundStates = nextHiddenBackgroundStates;
  if (hiddenState.ariaHidden === null) {
    element.removeAttribute("aria-hidden");
  } else {
    element.setAttribute("aria-hidden", hiddenState.ariaHidden);
  }
  (element as HTMLElement & { inert?: boolean }).inert = hiddenState.inert;
};

/**
 * 指定のModalが最前面か判定する
 * カートのシートの上に確認を重ねたとき、Escape 1回で2枚とも閉じないようにする
 * @param {string} modalId - ModalのID
 * @returns {boolean} 最前面の場合true
 */
const isTopmostModal = (modalId: string) =>
  openModalIds[openModalIds.length - 1] === modalId;

/**
 * 同じ描画で後から開くModalのルートか判定する
 * カートのシートと確認を同じ描画で開くと、先に処理したシートが確認まで背景として隠すため、背景から除外する
 * @param {Element} element - body直下の要素
 * @returns {boolean} まだ重なり順へ登録されていないModalのルートの場合true
 */
const isModalRootOpeningLater = (element: Element) => {
  const elementModalId = element.getAttribute("data-modal-id");
  return elementModalId !== null && !openModalIds.includes(elementModalId);
};

/**
 * 全画面モーダル
 * Escape・Tabのfocus trap・browser backは、重なっているModalのうち最前面だけが処理する
 * @returns {JSX.Element | null} モーダル
 */
export const Modal = ({
  accessibilityDescribedBy,
  accessibilityLabel,
  accessibilityLabelledBy,
  children,
  closeOnBrowserBack = false,
  dismissible = true,
  initialFocusRef,
  onRequestClose,
  testID,
  trapBrowserBack = false,
  transparent = false,
  visible = true,
}: PrimitiveProps) => {
  /** dialogルート */
  const dialogRef = useRef<HTMLDivElement | null>(null);
  /** 最新のclose処理 */
  const onRequestCloseRef = useRef(onRequestClose);
  /** 最新のdismiss可否 */
  const dismissibleRef = useRef(Boolean(dismissible));
  /** browser backをdialog closeとして扱うか */
  const closeOnBrowserBackRef = useRef(Boolean(closeOnBrowserBack));
  /** browser backをdialog表示中だけ吸収するか */
  const trapBrowserBackRef = useRef(Boolean(trapBrowserBack));
  /** 最新の初期focus指定 */
  const initialFocusRefRef = useRef(initialFocusRef);
  /** 重なり順とbrowser back用の履歴を識別するID */
  const modalIdRef = useRef(
    `ikomasai-modal-${Math.random().toString(36).slice(2)}`,
  );

  onRequestCloseRef.current = onRequestClose;
  dismissibleRef.current = Boolean(dismissible);
  closeOnBrowserBackRef.current = Boolean(closeOnBrowserBack);
  trapBrowserBackRef.current = Boolean(trapBrowserBack);
  initialFocusRefRef.current = initialFocusRef;

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    /** dialogを開く直前にfocusされていた要素 */
    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    /** dialog以外のbody直下要素 */
    const backgroundElements = Array.from(document.body.children).filter(
      (element): element is HTMLElement =>
        element instanceof HTMLElement &&
        !element.contains(dialogRef.current) &&
        !isModalRootOpeningLater(element),
    );
    /** browser back用の履歴が現在も残っているか */
    let isModalHistoryEntryActive = false;
    /** このModalのID */
    const modalId = modalIdRef.current;

    if (openModalIds.length === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
    }
    openModalIds = [...openModalIds, modalId];
    backgroundElements.forEach(hideBackgroundElement);
    document.body.style.overflow = "hidden";

    /**
     * browser back制御用の履歴を1件だけ積む
     * @returns {void}
     */
    const pushModalHistoryEntry = () => {
      window.history.pushState(
        {
          ...(window.history.state ?? {}),
          __ikomasaiModalId: modalId,
        },
        "",
        window.location.href,
      );
      isModalHistoryEntryActive = true;
    };

    if (
      (closeOnBrowserBackRef.current || trapBrowserBackRef.current) &&
      typeof window.history?.pushState === "function"
    ) {
      pushModalHistoryEntry();
    }

    /** focus可能要素のselector */
    const focusableSelector = [
      "button:not([disabled])",
      "[href]",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");

    /**
     * dialog内のfocus可能要素を返す
     * @returns focus可能要素
     */
    const getFocusableElements = (): HTMLElement[] =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          focusableSelector,
        ) ?? [],
      ).filter(
        (element) =>
          element.getAttribute("aria-hidden") !== "true",
      );

    /**
     * 最前面のときだけEscapeで閉じ、Tabのfocusをdialog内で循環させる
     * @param {KeyboardEvent} event - キー入力
     * @returns {void}
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopmostModal(modalId)) {
        return;
      }

      if (event.key === "Escape" && dismissibleRef.current) {
        event.preventDefault();
        onRequestCloseRef.current?.("escape");
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      /** 今のfocus先。tabIndex=-1の見出しへ初期focusしたときは先頭の操作より前にある */
      const activeElement = document.activeElement;
      /** focus先が先頭の操作より前にあるか（前にfocus可能な要素が無いので、既定の動作ではdialogの外へ抜ける） */
      const isBeforeFirstElement = activeElement instanceof Node
        && activeElement !== firstElement
        && Boolean(activeElement.compareDocumentPosition(firstElement) & Node.DOCUMENT_POSITION_FOLLOWING);
      if (event.shiftKey && (activeElement === firstElement || isBeforeFirstElement)) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    /**
     * 同一URLへ積んだdialog履歴をbrowser backで閉じる
     * @returns {void}
     */
    const handlePopState = () => {
      if (!isModalHistoryEntryActive) {
        return;
      }

      // 自分の履歴が戻されたかで判断するため、上に履歴を持たないModalが重なっていても吸収する
      if (trapBrowserBackRef.current) {
        if (window.history.state?.__ikomasaiModalId === modalId) {
          return;
        }

        isModalHistoryEntryActive = false;
        // 戻るたびに同じ1件を積み直し、履歴件数を増やさずdialog内へ留める。
        pushModalHistoryEntry();
        return;
      }

      if (!isTopmostModal(modalId)) {
        return;
      }

      isModalHistoryEntryActive = false;

      if (!dismissibleRef.current) {
        return;
      }

      onRequestCloseRef.current?.("back");
    };

    window.addEventListener("keydown", handleKeyDown);
    if (closeOnBrowserBackRef.current || trapBrowserBackRef.current) {
      window.addEventListener("popstate", handlePopState);
    }

    /** DOM描画後に明示指定または先頭操作へfocusする */
    const focusFrame = window.requestAnimationFrame(() => {
      const requestedInitialFocus =
        initialFocusRefRef.current?.current as HTMLElement | undefined;
      const initialFocusElement =
        requestedInitialFocus ?? getFocusableElements()[0] ?? dialogRef.current;
      initialFocusElement?.focus();
    });

    return () => {
      openModalIds = openModalIds.filter((openModalId) => openModalId !== modalId);
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
      backgroundElements.forEach(releaseBackgroundElement);
      if (openModalIds.length === 0) {
        document.body.style.overflow = bodyOverflowBeforeModals ?? "";
        bodyOverflowBeforeModals = null;
      }

      if (
        isModalHistoryEntryActive &&
        window.history.state?.__ikomasaiModalId === modalId
      ) {
        window.history.back();
      }

      // 開く前のfocus先が、まだ開いている下のModalに隠されている場合は、その下のModalへfocusを移す
      const isPreviousFocusReachable =
        previouslyFocusedElement?.isConnected &&
        !previouslyFocusedElement.closest('[aria-hidden="true"]');
      const remainingTopModalRoot = document.querySelector<HTMLElement>(
        `[data-modal-id="${openModalIds[openModalIds.length - 1]}"]`,
      );
      if (isPreviousFocusReachable) {
        previouslyFocusedElement.focus();
      } else if (remainingTopModalRoot) {
        remainingTopModalRoot.focus();
      }
    };
  }, [visible]);

  if (!visible || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      aria-describedby={accessibilityDescribedBy}
      aria-label={accessibilityLabel}
      aria-labelledby={accessibilityLabelledBy}
      aria-modal="true"
      className={`web-modal-root ${transparent ? "web-modal-transparent" : ""}`}
      data-modal-id={modalIdRef.current}
      data-testid={testID}
      onMouseDown={(event) => {
        if (
          (event.target === event.currentTarget ||
            event.target === dialogRef.current?.firstElementChild) &&
          dismissibleRef.current
        ) {
          onRequestCloseRef.current?.("backdrop");
        }
      }}
      ref={dialogRef}
      role="dialog"
      tabIndex={-1}
    >
      {children}
    </div>,
    document.body,
  );
};

/**
 * 現在のブラウザ表示領域を監視する
 * @returns {{width: number, height: number, scale: number, fontScale: number}} 表示領域
 */
export const useWindowDimensions = () => {
  const readDimensions = () => ({
    width: typeof window === "undefined" ? 390 : window.innerWidth,
    height: typeof window === "undefined" ? 844 : window.innerHeight,
    scale: typeof window === "undefined" ? 1 : window.devicePixelRatio || 1,
    fontScale: 1,
  });
  const [dimensions, setDimensions] = useState(readDimensions);

  useEffect(() => {
    const handleResize = () => setDimensions(readDimensions());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return dimensions;
};

/** Webプラットフォーム情報 */
export const Platform = {
  OS: "web",
};

/**
 * アニメーションスタイルを購読して描画する
 * @returns {JSX.Element} アニメーションView
 */
const AnimatedView = forwardRef<HTMLDivElement, PrimitiveProps>(
  ({ style, ...props }, ref) => {
    const [, setRevision] = useState(0);
    const animatedValues = useMemo(() => collectAnimatedValues(style), [style]);

    useEffect(
      () =>
        animatedValues.length > 0
          ? (() => {
              const unsubscribers = animatedValues.map((value) =>
                value.subscribe(() => setRevision((revision) => revision + 1)),
              );
              return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
            })()
          : undefined,
      [animatedValues],
    );

    return <View {...props} ref={ref} style={style} />;
  },
);
AnimatedView.displayName = "AnimatedView";

/** アニメーション実行契約 */
type WebAnimation = {
  start: (callback?: (result: { finished: boolean }) => void) => void;
  stop: () => void;
};

/**
 * 単一の時間アニメーションを作成する
 * @param {WebAnimatedValue} value - 更新対象
 * @param {{toValue: number, duration?: number}} config - アニメーション設定
 * @returns {WebAnimation} アニメーション
 */
const createTimingAnimation = (
  value: WebAnimatedValue,
  config: {
    toValue: number;
    duration?: number;
    useNativeDriver?: boolean;
  },
): WebAnimation => {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  return {
    start: (callback) => {
      stopped = false;
      timerId = setTimeout(() => {
        if (stopped) {
          callback?.({ finished: false });
          return;
        }
        value.setValue(config.toValue);
        callback?.({ finished: true });
      }, Math.max(0, Number(config.duration) || 0));
    },
    stop: () => {
      stopped = true;
      if (timerId) {
        clearTimeout(timerId);
      }
    },
  };
};

/**
 * 複数アニメーションを順番に実行する
 * @param {WebAnimation[]} animations - アニメーション一覧
 * @returns {WebAnimation} 合成アニメーション
 */
const createSequenceAnimation = (animations: WebAnimation[]): WebAnimation => {
  let stopped = false;
  return {
    start: (callback) => {
      stopped = false;
      let index = 0;
      const runNext = () => {
        if (stopped || index >= animations.length) {
          callback?.({ finished: !stopped });
          return;
        }
        const animation = animations[index];
        index += 1;
        animation.start(runNext);
      };
      runNext();
    },
    stop: () => {
      stopped = true;
      animations.forEach((animation) => animation.stop());
    },
  };
};

/**
 * 複数アニメーションを同時実行する
 * @param {WebAnimation[]} animations - アニメーション一覧
 * @returns {WebAnimation} 合成アニメーション
 */
const createParallelAnimation = (animations: WebAnimation[]): WebAnimation => ({
  start: (callback) => {
    if (animations.length === 0) {
      callback?.({ finished: true });
      return;
    }
    let remaining = animations.length;
    let allFinished = true;
    animations.forEach((animation) =>
      animation.start((result) => {
        allFinished = allFinished && result.finished;
        remaining -= 1;
        if (remaining === 0) {
          callback?.({ finished: allFinished });
        }
      }),
    );
  },
  stop: () => animations.forEach((animation) => animation.stop()),
});

/** Web向けアニメーションAPI */
export const Animated = Object.freeze({
  Value: WebAnimatedValue,
  View: AnimatedView,
  timing: createTimingAnimation,
  sequence: createSequenceAnimation,
  parallel: createParallelAnimation,
});

/**
 * 画面の安全領域（ノッチ・ホームバー・角丸）の内側へ中身を収めるView
 * `viewport-fit=cover`で描画領域が画面の端まで広がるため、env(safe-area-inset-*)を内側の余白にする
 * @returns {JSX.Element} DOMレイアウト
 */
export const SafeAreaView = forwardRef<HTMLDivElement, PrimitiveProps>(
  ({ className = "", ...props }, ref) => (
    <View {...props} className={`web-safe-area ${className}`.trim()} ref={ref} />
  ),
);
SafeAreaView.displayName = "SafeAreaView";
